import { api, API_BASE } from '../lib/api';
import { auth } from '../lib/firebase';
import type { Folder } from '../types';

function parseFolder(data: any): Folder {
  return {
    id: data.id,
    name: data.name ?? '',
    description: data.description ?? '',
    imageUrl: data.imageUrl ?? '',
    createdBy: data.createdBy,
    members: data.members
      ? Object.fromEntries(
          (data.members as any[]).map((m: any) => [m.userId, m.role]),
        )
      : data.role
        ? { [data.createdBy]: data.role }
        : {},
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function createFolder(
  _userId: string,
  data: { name: string; description?: string },
): Promise<string> {
  const res = await api<{ id: string }>('/api/collections', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return res.id;
}

export async function getUserFolders(_userId: string): Promise<Folder[]> {
  const results = await api<any[]>('/api/collections');
  return results.map(parseFolder);
}

export async function getFolder(folderId: string): Promise<Folder | null> {
  try {
    const data = await api<any>(`/api/collections/${folderId}`);
    return parseFolder(data);
  } catch {
    return null;
  }
}

export async function updateFolder(
  folderId: string,
  data: Partial<Pick<Folder, 'name' | 'description' | 'members'>>,
): Promise<void> {
  const body: Record<string, unknown> = {};
  if (data.name !== undefined) body.name = data.name;
  if (data.description !== undefined) body.description = data.description;
  if (data.members !== undefined) {
    body.members = Object.entries(data.members).map(([userId, role]) => ({
      userId,
      role,
    }));
  }
  await api(`/api/collections/${folderId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteFolder(folderId: string): Promise<void> {
  await api(`/api/collections/${folderId}`, { method: 'DELETE' });
}

export async function streamFolderSummary(
  folderId: string,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = await auth.currentUser?.getIdToken();

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/collections/${folderId}/summary`, {
      signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch (e: any) {
    if (e?.name === 'AbortError') return;
    onError(e?.message || 'Network error');
    return;
  }

  if (!res.ok) {
    const text = await res.text();
    onError(text || `HTTP ${res.status}`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    onError('No response body');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const parsed = JSON.parse(line.slice(6));
        if (parsed.chunk) onChunk(parsed.chunk);
        if (parsed.done) { onDone(); return; }
        if (parsed.error) { onError(parsed.error); return; }
      } catch {
        // ignore malformed lines
      }
    }
  }
  // Stream closed without an explicit done event (proxy timeout, connection reset, etc.)
  onDone();
}
